import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/** Ant Table owns rendering; expose its overflowing viewport to keyboard users. */
export function useTableScrollRegions() {
  const root = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const content = root.current;
    if (!content) return;
    const observed = new Set<HTMLElement>();
    const owned = new Set<HTMLElement>();
    const update = () => {
      const regions = [
        ...content.querySelectorAll<HTMLElement>(".ant-table-content, .ant-table-body"),
        ...content.querySelectorAll<HTMLElement>(".table-wrap"),
      ];
      regions.forEach((region) => {
        if (!observed.has(region)) {
          observed.add(region);
          resize?.observe(region);
        }
        const hasInnerViewport = region.matches(".table-wrap") && region.querySelector('.ant-table-content[tabindex="0"], .ant-table-body[tabindex="0"]');
        const overflowing = region.scrollWidth > region.clientWidth && !hasInnerViewport;
        if (overflowing && !region.hasAttribute("tabindex")) {
          region.tabIndex = 0;
          region.setAttribute("role", "region");
          region.setAttribute("aria-label", t("common.scrollTable"));
          owned.add(region);
        } else if (!overflowing && owned.has(region)) {
          region.removeAttribute("tabindex");
          region.removeAttribute("role");
          region.removeAttribute("aria-label");
          owned.delete(region);
        }
      });
      observed.forEach((region) => {
        if (!content.contains(region)) {
          resize?.unobserve(region);
          observed.delete(region);
          owned.delete(region);
        }
      });
    };
    const resize = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(update);
    const mutations = new MutationObserver(update);
    mutations.observe(content, { childList: true, subtree: true });
    update();
    return () => {
      mutations.disconnect();
      resize?.disconnect();
      owned.forEach((region) => {
        region.removeAttribute("tabindex");
        region.removeAttribute("role");
        region.removeAttribute("aria-label");
      });
    };
  }, [t]);
  return root;
}
